import type { ButtonProps } from '@components/button/button'
import type { ButtonTrackingProps } from '@components/button/props'
import { Desktop, Mobile } from '@components/responsive'
import { DownloadApp } from '@contentful-components/forms/download-app'
import type { ActionDownloadAppFragment } from '@graphql/fragments/ActionDownloadAppFragment.graphql'
import { useStores } from '@lib/store/useStores'
import type { WithTestId } from '@lib/utils/testid'
import { observer } from 'mobx-react-lite'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'

import { ActionRemoteUrl } from './action-remote-url'

export const ActionDownloadApp: FunctionComponent<Props> = observer(
    ({ title: titleFromProps, preset, ...props }) => {
        const stores = useStores()
        const router = useRouter()

        const { linkingStore } = stores

        // Get the current page path for attribution tracking
        const pagePath = router.asPath.split('?')[0].split('#')[0]

        // Check if there's already utm_content in the URL (external attribution)
        const urlParams = new URLSearchParams(router.asPath.split('?')[1] || '')
        const hasExternalUtmContent = urlParams.has('utm_content')

        // Use page-contextualized link only if no external utm_content exists
        const link = hasExternalUtmContent
            ? linkingStore.appDownloadLink
            : linkingStore.getAppDownloadLinkWithPageContext(pagePath)

        const { buttonProps } = props

        return (
            <>
                <Mobile>
                    <ActionRemoteUrl
                        {...props}
                        {...buttonProps}
                        url={
                            link ?? process.env.NEXT_PUBLIC_ORGANIC_DOWNLOAD_URL
                        }
                        preset={preset}
                        title={titleFromProps}
                        data-testid='getApp.button'
                    />
                </Mobile>
                <Desktop>
                    <DownloadApp
                        {...props}
                        preset={preset}
                        title={titleFromProps}
                        pagePath={pagePath}
                    />
                </Desktop>
            </>
        )
    },
)

type Props = WithTestId<
    ButtonTrackingProps &
        ActionDownloadAppFragment & {
            size?: 'small' | 'medium'
            buttonProps?: Partial<ButtonProps>
            loading?: boolean
        }
>
